import React, { useEffect, useState } from "react";
import api, { fmtMoney, getToken, API } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileText, FileSpreadsheet, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";

function StatusBadge({ s }) {
  const map = {
    success: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
    pending: "bg-amber-500/10 text-amber-600 border-amber-500/20",
    rejected: "bg-rose-500/10 text-rose-600 border-rose-500/20",
  };
  return <span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium capitalize ${map[s] || "bg-slate-500/10 text-slate-500"}`}>{s}</span>;
}

export default function UserStatement() {
  const [txns, setTxns] = useState([]);

  useEffect(() => {
    api.get("/user/transactions").then(({ data }) => setTxns(data)).catch(() => {});
  }, []);

  const downloadBlob = async (path, filename) => {
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="statement-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Statement</h1>
          <p className="text-sm text-slate-500 mt-1">All your transactions in one place.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-xl" onClick={() => downloadBlob("/user/statement.csv", "naxopay_statement.csv")} data-testid="download-statement-csv-btn">
            <FileSpreadsheet className="h-4 w-4 mr-2" /> CSV
          </Button>
          <Button className="rounded-xl bg-blue-600 hover:bg-blue-700" onClick={() => downloadBlob("/user/statement.pdf", "naxopay_statement.pdf")} data-testid="download-statement-pdf-btn">
            <Download className="h-4 w-4 mr-2" /> PDF
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200 dark:border-slate-800">
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">UTR</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Slip</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {txns.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No transactions yet</td></tr>}
              {txns.map((t) => (
                <tr key={t.id} data-testid={`statement-row-${t.id}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(t.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" })}</td>
                  <td className="px-4 py-3 capitalize font-medium text-slate-900 dark:text-white">{t.type}</td>
                  <td className={`px-4 py-3 font-mono-data font-semibold ${t.type === "payin" ? "text-emerald-600" : "text-slate-900 dark:text-white"}`}>{t.type === "payin" ? "+" : "-"}{fmtMoney(t.amount)}</td>
                  <td className="px-4 py-3 font-mono-data text-xs text-slate-500">{t.utr || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge s={t.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => downloadBlob(`/user/transactions/${t.id}/slip.pdf`, `receipt_${t.id.slice(0, 8)}.pdf`)} className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 text-xs font-medium" data-testid={`slip-btn-${t.id}`}>
                      <Receipt className="h-3.5 w-3.5" /> PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
